import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { AdminSearchItem, AdminSearchNode, AdminSearchOption, AdminSearchResult, AdminSearchType } from './reseller-admin-panel.model';
import { ResellerAdminPanelService } from 'app/services/reseller-admin-panel.service';
import { AuthService } from 'app/services/auth/auth.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'app-reseller-admin-panel',
    templateUrl: './reseller-admin-panel.component.html',
    styleUrls: ['./reseller-admin-panel.component.css']
})
export class ResellerAdminPanelComponent {

    searchOptions: AdminSearchOption[] = [
        { value: AdminSearchType.Driver, label: this.translate.instant('ResellerAdminPanel.Driver') },
        { value: AdminSearchType.Vehicle, label: this.translate.instant('ResellerAdminPanel.Vehicle') },
        { value: AdminSearchType.Device, label: this.translate.instant('ResellerAdminPanel.Device') },
        { value: AdminSearchType.Terminal, label: 'RDU' },
        { value: AdminSearchType.Client, label: this.translate.instant('ResellerAdminPanel.Client') },
    ];

    searchType: AdminSearchType = this.searchOptions[0].value;
    searchText = '';

    loading = false;
    searched = false;
    error = '';
    results: AdminSearchResult[] = [];
    resultType: AdminSearchType = this.searchType;

    sortColumn: keyof AdminSearchResult | '' = '';
    sortAsc = true;

    readonly minChars = 3;

    get detailsHeader(): string {
        switch (this.resultType) {
            case AdminSearchType.Driver:
                return this.translate.instant('ResellerAdminPanel.CardNumber');
            case AdminSearchType.Vehicle:
                return 'VIN';
            case AdminSearchType.Terminal:
                return this.translate.instant('ResellerAdminPanel.RegMac');
            default:
                return this.translate.instant('ResellerAdminPanel.Details');
        }
    }

    get hasDetails(): boolean {
        return this.results.some(row => !!row.itemDetails);
    }

    constructor(
        private readonly resellerAdminPanelService: ResellerAdminPanelService,
        private readonly authService: AuthService,
        private readonly router: Router,
        private readonly translate: TranslateService,
    ) { }

    onSearch(): void {
        const query = this.searchText.trim();
        if (query.length < this.minChars) {
            return;
        }
        const type = this.searchType;
        this.loading = true;
        this.searched = true;
        this.error = '';
        this.results = [];
        this.sortColumn = '';
        this.resellerAdminPanelService.search(type, query).subscribe({
            next: (data) => {
                this.resultType = type;
                this.results = this.flatten(data, [], type);
                this.loading = false;
            },
            error: () => {
                this.error = this.translate.instant('ResellerAdminPanel.SearchFailed');
                this.loading = false;
            }
        });
    }

    sortBy(column: keyof AdminSearchResult): void {
        this.sortAsc = this.sortColumn === column ? !this.sortAsc : true;
        this.sortColumn = column;
        const direction = this.sortAsc ? 1 : -1;
        this.results = [...this.results].sort((a, b) =>
            String(a[column]).localeCompare(String(b[column]), undefined, { numeric: true, sensitivity: 'base' }) * direction);
    }

    // Switch to the row's company and open the matching list page filtered by the item.
    onRowClick(row: AdminSearchResult): void {
        this.authService.changeCompanyId(row.companyId);
        const target = this.routeForType(this.resultType);
        if (!target) {
            return;
        }
        const queryParams = (this.resultType !== AdminSearchType.Client && row.filterValue)
            ? { filterValue: row.filterValue }
            : {};
        // Give the company switch time to propagate before navigating.
        setTimeout(() => {
            this.router.navigate([target], { queryParams });
        }, 500);
    }

    private routeForType(type: AdminSearchType): string | null {
        switch (type) {
            case AdminSearchType.Driver:
                return '/drivers';
            case AdminSearchType.Vehicle:
                return '/vehicles';
            case AdminSearchType.Device:
                return '/devices';
            case AdminSearchType.Client:
                return '/dashboard';
            default:
                return null;
        }
    }

    private flatten(nodes: AdminSearchNode[], parentPath: string[], type: AdminSearchType): AdminSearchResult[] {
        const rows: AdminSearchResult[] = [];
        for (const node of nodes || []) {
            const path = [...parentPath, node.clientName];
            for (const item of node.items || []) {
                rows.push({
                    itemLabel: this.getItemLabel(item, type),
                    itemDetails: this.getItemDetails(item, type),
                    itemId: item.id,
                    companyId: node.clientId,
                    companyName: node.clientName,
                    companyPath: path.join(' / '),
                    filterValue: this.getFilterValue(item, type),
                });
            }
            if (node.children && node.children.length) {
                rows.push(...this.flatten(node.children, path, type));
            }
        }
        return rows;
    }

    private getItemLabel(item: AdminSearchItem, type: AdminSearchType): string {
        switch (type) {
            case AdminSearchType.Driver:
                return `${item.firstName || ''} ${item.lastName || ''}`.trim();
            case AdminSearchType.Vehicle:
                return item.registrationNumber || '';
            case AdminSearchType.Device:
                return item.imei || '';
            case AdminSearchType.Terminal:
                return item.name || item.mac || '';
            case AdminSearchType.Client:
                return item.name || '';
            default:
                return String(item.id);
        }
    }

    // Secondary info shown in the Details column.
    private getItemDetails(item: AdminSearchItem, type: AdminSearchType): string {
        switch (type) {
            case AdminSearchType.Driver:
                return item.cardNumber || '';
            case AdminSearchType.Vehicle:
                return item.identificationNumber || '';
            case AdminSearchType.Terminal:
                return [item.registrationNumber, item.mac].filter(Boolean).join(' / ');
            default:
                return '';
        }
    }

    // Value used to filter the target list page after switching company.
    private getFilterValue(item: AdminSearchItem, type: AdminSearchType): string {
        switch (type) {
            case AdminSearchType.Driver:
                return `${item.firstName || ''} ${item.lastName || ''}`.trim();
            case AdminSearchType.Vehicle:
                return item.registrationNumber || item.identificationNumber || '';
            case AdminSearchType.Device:
                return item.imei || '';
            case AdminSearchType.Terminal:
                return item.registrationNumber || item.name || item.mac || '';
            case AdminSearchType.Client:
                return item.name || '';
            default:
                return '';
        }
    }
}
